<script lang="ts">
	//Hooks
	import { createEventDispatcher, onMount } from 'svelte';
	// Format
	import { format } from 'svelte-i18n';
	// Design system components
	import { Select, Datepicker, PrevNextSwitch } from '@Shelly/shelly-design-system';
	// Helpers
	import {
		addDate,
		dateFormat,
		startsWeekOnSun,
		getTimeFormat,
		dateLabels,
		getMonthsOptions,
		timeFormatUS,
	} from 'src/helpers/date_helper';
	import { debounce, deepCopy } from 'src/helpers/utilities';
	import {
		rangeDates,
		RANGE_OPTIONS,
		getInitLoadedPeriodStatistics,
		getBillingDate,
	} from 'src/helpers/statistics/ui/chart_control_helpers';
	import type {
		RangeOptionInterface,
		ChartControlsDataInterface,
	} from 'src/helpers/statistics/ui/chart_control_helpers';
	//Types
	import type { TimeZoneInterface } from 'src/types/Chart';

	export let chartControlID: string,
		timeZone: TimeZoneInterface = null,
		enabledHourControl = true,
		enabledBillingDate = true,
		overallPage = false;

	export let showFormControls = true; // Hide form controls but use the component to render the chart data with default period

	export const changeDateRangeOnClick = (e: CustomEvent) => {
			const clickAction = e.detail.clickAction;
			if (clickAction) {
				const range = rangeControlOptions.find((a) => a.period == clickAction);
				if (range && range.enabled && e.detail.value != null) {
					rangeOption = range;
					changeTimeRange('click', e.detail.date, clickAction == 'hour');
				}
			}
		},
		getCurrentTime = (): { from: Date; to: Date } => {
			return currentTime;
		};

	let today = timeZone?.date || new Date();

	const firstDayOfWeek = startsWeekOnSun() ? 0 : 1,
		rangeControlOptions = deepCopy(RANGE_OPTIONS),
		weekdays: any = dateLabels('days'),
		defaultMonthsOptions = getMonthsOptions(),
		dispatch = createEventDispatcher(),
		formatD = timeFormatUS() ? 'm.d.Y' : 'd.m.Y',
		fullYear = today.getFullYear(),
		years = [`${fullYear - 3}`, `${fullYear - 2}`, `${fullYear - 1}`, `${fullYear}`],
		labelFormat = (range: RangeOptionInterface) => {
			switch (range.period) {
				case 'hour':
					return `${dateFormat(currentTime.to, formatD)} ${getTimeFormat(
						fromHour,
					)} - ${getTimeFormat(fromHour + 1)}`;
				case 'day':
					return `${dateFormat(currentTime.from, formatD)}`;
				default:
					const dateFrom = dateFormat(currentTime.from, formatD);
					if (dateFrom == dateFormat(currentTime.to, formatD)) {
						return dateFrom;
					}
					return `${dateFrom} - ${dateFormat(currentTime.to, formatD)}`;
			}
		},
		onSelect = (time: { from: Date; to: Date }, type = 'change', debouncing = false) => {
			backForwardNavLabel = labelFormat(rangeOption);
			dpDateMode = [addDate(time.from, 'd', 0, true)];
			disableBackwardNavButton = time.from.getTime() < minDate.getTime();
			disableForwardNavButton = time.to.getTime() > today.getTime();
			const chartControlsData: ChartControlsDataInterface = {
				time,
				format: 'Y-m-d H:i:s',
				rangeOption,
				currentPeriod: rangeOption.period,
			};
			if (debouncing) {
				debounce(() => dispatch(type, chartControlsData), 500);
			} else dispatch(type, chartControlsData);
		},
		calendarSelect = (date: Date) => {
			/**
			 * Update selected date in day, week, month, year period comming from calendar
			 */
			if (rangeOption.period == 'hour') {
				date.setHours(fromHour, 0, 0, 0);
			}
			changeTimeRange('init', date);
		},
		periodSelect = (period: string, type = 'change') => {
			today = timeZone?.date || new Date();
			rangeOption = rangeControlOptions.find((a) => a.period == period);
			if (rangeOption.period == 'billing_date') {
				changeTimeRange('init', addDate(getBillingDate(), 'd', 0, true));
			} else if (rangeOption.period == 'hour') {
				calendarSelect(today);
			} else if (rangeOption.period == 'custom') {
				onSelect(currentCustomTime, type);
			} else {
				changeTimeRange('init', today);
			}
		},
		hoursSelect = (e: CustomEvent) => {
			const hourRange = hours.find((i) => i.value == e.detail.value);
			fromHour = hourRange.value;
			currentTime.from.setHours(fromHour, 0, 0, 0);
			changeTimeRange('init');
		},
		monthSelect = (e: CustomEvent) => {
			const month = new Date(currentTime.from.getTime()).setMonth(e.detail.value, 1),
				d = rangeDates(new Date(month), rangeOption, 'init', true);
			let date = d.from;
			if (date > today) date = addDate(date, 'y', -1);
			else {
				const nextYear = addDate(date, 'y', 1);
				if (nextYear <= today) date = nextYear;
			}
			changeTimeRange('init', date);
		},
		yearSelect = (e: CustomEvent) => {
			currentTime.from.setFullYear(e.detail, 0, 1);
			changeTimeRange('init');
		},
		prevNextSelect = (type: 'subtract' | 'add' | 'init' | 'click') => {
			changeTimeRange(type, null, rangeOption.period == 'hour', true);
		},
		changeTimeRange = (
			type: 'subtract' | 'add' | 'init' | 'click',
			date: Date = null,
			updateFromHour = false,
			debouncing = false,
		) => {
			const d = rangeDates(date || currentTime.from, rangeOption, type, true);
			currentTime.from = d.from;
			currentTime.to = d.to;
			if (updateFromHour) fromHour = currentTime.from.getHours();
			onSelect(currentTime, 'change', debouncing);
		},
		createHours = () => {
			const hours = [];
			for (let i = 0; i < 24; ++i) {
				hours[i] = {
					title: `${getTimeFormat(i)} - ${getTimeFormat((i + 1) % 24)}`,
					value: i,
				};
			}
			return hours;
		},
		createMonths = () => {
			const currentMonth = today.getMonth(),
				monthsLength = defaultMonthsOptions.length,
				months = [];
			for (let i = 0; i < monthsLength; ++i) {
				const month = defaultMonthsOptions[i],
					index = currentMonth - i + (currentMonth < month.value ? monthsLength : 0);
				months[index] = month;
			}
			return months;
		};
	rangeControlOptions.find((a) => a.period == 'hour').enabled = enabledHourControl;
	rangeControlOptions.find((a) => a.period == 'billing_date').enabled = enabledBillingDate;

	let disableForwardNavButton = false,
		disableBackwardNavButton = false,
		rangeOption: RangeOptionInterface = getInitLoadedPeriodStatistics(rangeControlOptions),
		minDate = addDate(today, 'y', -3),
		minCustomDate = minDate,
		maxCustomDate = today,
		fromHour = today.getHours(),
		currentTime = rangeDates(today, rangeOption, 'init', true),
		currentCustomTime = {
			from: addDate(today, 'd', -1, true),
			to: addDate(today, 'd', 0, true),
		},
		backForwardNavLabel: string = '',
		dpDateMode = [today],
		hours = createHours(),
		months = createMonths();

	onMount(() => periodSelect(rangeOption.period, 'init'));
</script>

<div class="chart-controls-wrapper" class:chart-controls-overall={overallPage}>
	{#if showFormControls}
		<Select
			size="m"
			value={{
				title: $format(`option_${rangeOption.period}`),
				value: rangeOption.period,
			}}
			id="select-chart-range"
			aria-label={$format('select_chart_range', { default: 'Chart range' })}
			icon="fas fa-clock-rotate-left"
			class={overallPage ? `width-md-l width-sm-full` : 'width-full'}
			options={rangeControlOptions
				.filter((i) => i.enabled)
				.map((i) => ({ title: $format(`option_${i.period}`), value: i.period }))}
			on:change={(e) => {
				const period = e.detail['value'];
				if (rangeOption.period != period) {
					periodSelect(period);
				}
			}}
		/>
		{#if /^(hour|day|week)$/.test(rangeOption.period)}
			<Datepicker
				id={`chart-range-dp-${chartControlID}`}
				dateFormat={formatD}
				mode="single"
				{minDate}
				maxDate={today}
				class={overallPage ? `width-md-l width-sm-full` : 'mb-s'}
				{defaultMonthsOptions}
				options={{
					disable: [(date) => rangeOption.period == 'week' && date.getDay() != firstDayOfWeek],
					locale: {
						firstDayOfWeek,
						weekdays: {
							shorthand: weekdays,
							longhand: weekdays,
						},
					},
				}}
				value={dpDateMode}
				aria-label={$format('datepicker_hour_day_week', {
					default: 'Datepicker for hour, day, week',
				})}
				a11yProps={{
					inputAriaLabel: $format('input_enter_date', { default: 'Enter date' }),
					selectMonthAriaLabel: $format('select_month', {
						default: 'Select month',
					}),
					selectYearAriaLabel: $format('select_year', { default: 'Select year' }),
				}}
				on:change={(e) => {
					const date = e.detail[0][0];
					if (date && dpDateMode && date.getTime() != dpDateMode[0].getTime())
						calendarSelect(new Date(date));
				}}
			/>
		{/if}
		{#if rangeOption.period === 'month'}
			<Select
				id="select-month"
				aria-label={$format('select_month', { default: 'Select month' })}
				size="m"
				class={overallPage ? `width-md-m width-sm-full` : 'mb-s'}
				width="full"
				icon="fa-light fa-calendar"
				options={months}
				value={months.find((i) => i.value == currentTime.from.getMonth())}
				on:change={monthSelect}
			/>
		{:else if rangeOption.period === 'year'}
			<Select
				id="select-year"
				aria-label={$format('select_year', { default: 'Select year' })}
				size="m"
				class={overallPage ? `width-md-m width-sm-full` : 'mb-s'}
				width="full"
				icon="fa-light fa-calendar"
				options={years}
				value={years.find((i) => i == currentTime.from.getFullYear().toString()) ||
					currentTime.from.getFullYear().toString()}
				on:change={yearSelect}
			/>
		{/if}
	{/if}

	<!-- We shoudl discuss this class setting here.
	 The <Toolbar> component in the DS has a lot more options
	 that can be used to make these here options way more elegant -->

	<div class={overallPage ? '' : 'd-flex'}>
		{#if rangeOption.period === 'custom'}
			<Datepicker
				id={`chart-range-dp-${chartControlID}`}
				dateFormat={formatD}
				mode="range"
				{minDate}
				maxDate={today}
				class={overallPage ? `width-md-l width-sm-full` : 'mb-s'}
				{defaultMonthsOptions}
				options={{
					locale: {
						firstDayOfWeek,
						weekdays: {
							shorthand: weekdays,
							longhand: weekdays,
						},
					},
					disable: [(date) => !(date >= minCustomDate && date <= maxCustomDate)],
				}}
				aria-label={$format('datepicker_custom', {
					default: 'Custom datepicker',
				})}
				a11yProps={{
					inputAriaLabel: $format('input_enter_date', { default: 'Enter date' }),
					selectMonthAriaLabel: $format('select_month', {
						default: 'Select month',
					}),
					selectYearAriaLabel: $format('select_year', { default: 'Select year' }),
				}}
				value={[currentCustomTime.from, currentCustomTime.to]}
				on:change={(e) => {
					const from = e.detail[0][0],
						to = e.detail[0][1];
					if (from && to) {
						onSelect({
							from,
							to: addDate(addDate(to, 'd', 1), 's', -1, true),
						});
						minCustomDate = minDate;
						maxCustomDate = today;
					} else if (from) {
						const dtMinDate = addDate(addDate(from, 'y', -1), 'd', 1);
						const dtMaxDate = addDate(addDate(from, 'y', 1), 'd', -1);
						minCustomDate = dtMinDate < minDate ? minDate : dtMinDate;
						maxCustomDate = dtMaxDate > today ? today : dtMaxDate;
					}
				}}
			/>
		{:else if rangeOption.period != 'hour'}
			<PrevNextSwitch
				class={overallPage ? `width-md-l width-sm-full` : 'width-full'}
				disablePrev={disableBackwardNavButton}
				disableNext={disableForwardNavButton}
				label={backForwardNavLabel}
				on:next={() => prevNextSelect('add')}
				on:prev={() => prevNextSelect('subtract')}
			/>
		{:else if rangeOption.period === 'hour'}
			<Select
				id="select-hour"
				aria-label={$format('select_hour', { default: 'Select hour' })}
				size="m"
				evNextSwitch
				class={overallPage ? `width-md-l width-sm-full` : 'width-full'}
				width="full"
				icon="fas fa-clock"
				options={hours}
				value={hours.find((i) => i.value == fromHour) || hours[0]}
				on:change={hoursSelect}
			/>
		{/if}
		<slot name="icon" />
	</div>
</div>

<style>
	.chart-controls-wrapper {
		display: contents;
	}

	.chart-controls-overall {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: flex-end;
		gap: var(--spacing-s);
		width: 100%;
	}

	@media screen and (max-width: 639px) {
		.chart-controls-overall {
			flex-direction: column;
			align-items: stretch;
		}

		.chart-controls-overall > div {
			width: 100%;
		}
	}
</style>
